import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Calculator, DollarSign, PiggyBank, LineChart, BarChart2 } from 'lucide-react';

const FinancialToolsPage: React.FC = () => {
  const navigate = useNavigate();

  const tools = [
    {
      title: 'ROI Calculator',
      description: 'Calculate potential return on investment for different locations',
      icon: Calculator,
      path: '/roi-calculator',
      color: 'text-primary'
    },
    {
      title: 'Financing Options',
      description: 'Explore available financing and loan options',
      icon: DollarSign,
      path: '/financing-options',
      color: 'text-secondary'
    },
    {
      title: 'Budget Planner',
      description: 'Plan and track your business location budget',
      icon: PiggyBank,
      path: '/budget-planner',
      color: 'text-accent'
    },
    {
      title: 'Cost Estimator',
      description: 'Estimate setup and operational costs',
      icon: BarChart2,
      path: '/cost-estimator',
      color: 'text-chart-1'
    },
    {
      title: 'Investment Analysis',
      description: 'Analyze long-term investment potential',
      icon: LineChart,
      path: '/investment-analysis',
      color: 'text-chart-2'
    }
  ];

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold text-primary">Financial Tools</h1>
        <p className="text-xl text-muted-foreground">
          Make informed financial decisions with our comprehensive suite of tools
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {tools.map((tool) => {
          const Icon = tool.icon;
          return (
            <button
              key={tool.path}
              onClick={() => navigate(tool.path)}
              className="bg-card/50 backdrop-blur-sm border border-border rounded-lg p-6 text-left hover:border-primary/50 transition-colors"
            >
              <Icon className={`${tool.color} mb-4`} size={32} />
              <h2 className="text-xl font-semibold mb-2">{tool.title}</h2>
              <p className="text-muted-foreground">{tool.description}</p>
            </button>
          );
        })}
      </div>

      <div className="bg-card/50 backdrop-blur-sm border border-border rounded-lg p-6">
        <h2 className="text-2xl font-semibold mb-4">Financial Insights</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="space-y-2">
            <h3 className="font-medium">Market Trends</h3>
            <p className="text-sm text-muted-foreground">
              Stay informed about current market conditions and investment opportunities
            </p>
          </div>
          <div className="space-y-2">
            <h3 className="font-medium">Risk Assessment</h3>
            <p className="text-sm text-muted-foreground">
              Evaluate potential risks and mitigation strategies
            </p>
          </div>
          <div className="space-y-2">
            <h3 className="font-medium">Growth Potential</h3>
            <p className="text-sm text-muted-foreground">
              Analyze long-term growth prospects and market expansion opportunities
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialToolsPage;